import { useEffect, useRef, useState } from 'react'
import { getApiBase } from './utils.js'

export function WaveCanvas({ sequence }) {
  const canvasRef = useRef(null)
  const animRef = useRef(null)
  const phaseRef = useRef(0)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    function draw() {
      const w = canvas.width
      const h = canvas.height
      ctx.fillStyle = '#0a0a0a'
      ctx.fillRect(0, 0, w, h)
      if (sequence?.length >= 2) {
        phaseRef.current += 0.005
        const phase = phaseRef.current
        ctx.strokeStyle = 'rgba(180,180,180,0.35)'
        ctx.lineWidth = 1
        ctx.beginPath()
        for (let x = 0; x < w; x++) {
          const idx = Math.floor((x / w) * sequence.length)
          const y = h * 0.35 - (sequence[idx] ?? 0) * h * 0.25
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
        }
        ctx.stroke()
        const delay = Math.floor(sequence.length * 0.15)
        ctx.strokeStyle = 'rgba(140,140,140,0.2)'
        ctx.beginPath()
        for (let i = 0; i < sequence.length - delay; i++) {
          const px = (sequence[i] + 1) * 0.5 * w
          const py = (sequence[i + delay] + 1) * 0.5 * h * 0.5 + h * 0.55
          i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)
        }
        ctx.stroke()
        const n = Math.min(sequence.length, 40)
        for (let i = 0; i < n; i++) {
          const val = sequence[i]
          const px = (i / n + Math.sin(phase + i * 0.3) * 0.02) * w
          const py = h * 0.5 + val * h * 0.2 + Math.cos(phase * 2 + i) * 3
          ctx.fillStyle = `rgba(200,200,200,${0.15 + Math.abs(val) * 0.25})`
          ctx.fillRect(px, py, 1.5, 1.5)
        }
      }
      animRef.current = requestAnimationFrame(draw)
    }
    draw()
    return () => cancelAnimationFrame(animRef.current)
  }, [sequence])

  return (
    <canvas ref={canvasRef} className="dm-canvas mobi-wave" width={480} height={140} />
  )
}

export function CurrentDream({ dream, asciiPattern, onOpenDetail }) {
  if (!dream) {
    return (
      <div className="mobi-dream">
        <span className="dm-section">CURRENT DREAM</span>
        <pre className="dm-terminal">
          <span className="dm-dim">awaiting signal...</span>
        </pre>
      </div>
    )
  }
  const pattern = asciiPattern || dream.ascii_pattern || []
  const boxWidth = 32
  return (
    <div className="mobi-dream">
      <div className="mobi-panel-head">
        <span className="dm-section">CURRENT DREAM</span>
        <button
          type="button"
          className="mobi-link"
          onClick={() => onOpenDetail?.({ kind: 'dream', dream })}
        >
          detail
        </button>
      </div>
      <div className="mobi-dream-id">#{dream.generation}</div>
      <pre className="dm-terminal mobi-ascii">
        <span className="dm-box">{'╭' + '─'.repeat(boxWidth) + '╮'}</span>
        {'\n'}
        {pattern.map((line, i) => (
          <span key={i}>
            <span className="dm-box">│ </span>
            {line.padEnd(boxWidth - 1)}
            <span className="dm-box">│</span>
            {'\n'}
          </span>
        ))}
        <span className="dm-box">{'╰' + '─'.repeat(boxWidth) + '╯'}</span>
      </pre>
      <div className="mobi-dream-gen-line dm-dim">
        {dream.generator_type || '—'}
        {dream.generator_hash ? ` · ${String(dream.generator_hash).slice(0, 8)}` : ''}
      </div>
    </div>
  )
}

export function FeedbackBar({ dreamId }) {
  const [sent, setSent] = useState(null)
  useEffect(() => {
    setSent(null)
  }, [dreamId])
  async function submit(feedback) {
    if (!dreamId || sent) return
    try {
      await fetch(`${getApiBase()}/api/dream/${dreamId}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedback }),
      })
      setSent(feedback)
    } catch {
      /* optional */
    }
  }
  return (
    <div className="dm-feedback">
      {['interesting', 'strange', 'boring'].map((f) => (
        <span
          key={f}
          className={`dm-fb${sent === f ? ' dm-fb-active' : ''}`}
          onClick={() => submit(f)}
          role="button"
          tabIndex={0}
        >
          {`[ ${f} ]`}
        </span>
      ))}
    </div>
  )
}
